/**
 * CUSTOMER-R4.15-K18 — canonical checkout availability → pay-now eligibility.
 */
export type CanonicalAvailability =
  | "IN_STOCK"
  | "LOW_STOCK"
  | "OUT_OF_STOCK"
  | "UNKNOWN"
  | "DISCONTINUED";

export function mapStockToCanonical(raw: string | null | undefined): CanonicalAvailability {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s || s === "unknown" || s === "stock_unknown" || s === "наявність уточнюється") return "UNKNOWN";
  if (s === "in_stock" || s === "available" || s === "в наявності") return "IN_STOCK";
  if (s === "low_stock" || s === "limited") return "LOW_STOCK";
  if (s === "out_of_stock" || s === "unavailable" || s === "немає в наявності" || s === "deleted") {
    return s === "deleted" ? "DISCONTINUED" : "OUT_OF_STOCK";
  }
  if (s === "discontinued" || s === "inactive") return "DISCONTINUED";
  return "UNKNOWN";
}

export function payNowAllowedForLine(input: {
  availability: CanonicalAvailability;
  requestedQty: number;
  availableQty?: number | null;
}): boolean {
  if (input.availability === "IN_STOCK") return true;
  if (input.availability === "LOW_STOCK") {
    if (typeof input.availableQty === "number" && Number.isFinite(input.availableQty)) {
      return input.requestedQty <= input.availableQty;
    }
    return false;
  }
  return false;
}

export type CheckoutAvailabilityLine = {
  productId: string;
  availability: CanonicalAvailability;
  requestedQty: number;
  availableQty?: number | null;
};

export function checkoutAvailabilityPolicy(
  lines: Array<{ availability: CanonicalAvailability; requestedQty: number; availableQty?: number | null }>,
): {
  payNowAllowed: boolean;
  managerConfirmOnly: boolean;
  reasons: CanonicalAvailability[];
} {
  const reasons = [...new Set(lines.map((l) => l.availability).filter((a) => a !== "IN_STOCK"))];
  const payNowAllowed = lines.length > 0 && lines.every((l) => payNowAllowedForLine(l));
  return {
    payNowAllowed,
    managerConfirmOnly: !payNowAllowed,
    reasons,
  };
}

/** Cart-line policy. Do not treat maxOrderQty as available stock. */
export function evaluateCheckoutAvailabilityPolicy(
  items: Array<{ id: string; qty: number; stockStatus?: string | null }>,
): {
  payNowAllowed: boolean;
  managerConfirmOnly: boolean;
  reasons: CanonicalAvailability[];
  lines: CheckoutAvailabilityLine[];
} {
  const lines: CheckoutAvailabilityLine[] = items.map((item) => ({
    productId: item.id,
    availability: mapStockToCanonical(item.stockStatus),
    requestedQty: item.qty,
    availableQty: null,
  }));
  return { ...checkoutAvailabilityPolicy(lines), lines };
}

export function formatAvailabilityLineReasons(lines: CheckoutAvailabilityLine[]): string {
  return lines
    .filter((line) => line.availability !== "IN_STOCK")
    .map((line) => `${line.productId}:${line.availability}`)
    .join(",")
    .slice(0, 400);
}
